/**
 * Authentication Guard Component
 * Protects routes by checking if user is authenticated.
 * Also initializes WebSocket connection when user becomes authenticated.
 *
 * Handles React StrictMode double-invoke by using a shared ref
 * so the WebSocket is only connected once across mount cycles.
 */

import React, { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { websocketService } from '../../services/websocketService';

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const wsConnectedRef = useRef(false);
  const authenticated = authService.isAuthenticated();

  useEffect(() => {
    if (!authenticated) return;

    // Connect WebSocket only once (survives StrictMode double-invoke)
    if (!wsConnectedRef.current) {
      const token = authService.getToken();
      if (token) {
        wsConnectedRef.current = true;
        websocketService.connect(token).catch(() => {
          // Silently ignore – service may not be running
        });
      }
    }

    return () => {
      // Only disconnect on real unmount, not StrictMode cleanup
      const timer = setTimeout(() => {
        if (wsConnectedRef.current) {
          websocketService.disconnect();
          wsConnectedRef.current = false;
        }
      }, 100);

      return void clearTimeout(timer);
    };
  }, [authenticated]);

  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export default AuthGuard;
